import type {Document, ProvidedSeeking, Rec, RecordOperation, SeekedSeeking, User, ViewableFile} from 'src/typings';
import type {Capture, Inventory, Match} from '../../inventory/types';
import type {Payment} from '../../payment/types';
import type {ComponentRenderConfig} from 'svelte-render';
import type {SvelteComponent} from 'svelte';

export interface SearchItemView<COMPONENT extends SvelteComponent> {
    renderConfig: ComponentRenderConfig<COMPONENT>;
    cssClass: string,
}

export type SearchItemViewProvider<ITEM, COMPONENT extends SvelteComponent> = (item: ITEM) => SearchItemView<COMPONENT>;

export type SearchItemViewMatcher<T extends SearchItem> = (value: Record<string, unknown>) => value is T;

export type SearchItem = Record<string, any>;

export interface SearchItemResolver<ITEM extends SearchItem, COMPONENT extends SvelteComponent> {
    matcher: SearchItemViewMatcher<ITEM>;
    provider: SearchItemViewProvider<ITEM, COMPONENT>;
}

export class SearchResultResolver {
    private registered: SearchItemResolver<any, any>[] = [];

    public register<ITEM extends SearchItem, COMPONENT extends SvelteComponent>(matcher: SearchItemViewMatcher<ITEM>, provider: SearchItemViewProvider<ITEM, COMPONENT>): SearchResultResolver {
        this.registered.push({matcher, provider});
        return this;
    }

    public resolve(value: Record<string, unknown>): SearchItemView<SvelteComponent> | null {
        return this.registered.find(({matcher}) => matcher(value)).provider(value);
    }
}

export function isAuthority(record: SearchItem): record is Rec {
    return 'type' in record && record.type === 'authority';
}

export function isDocument(record: SearchItem): record is Document {
    return 'type' in record && record.type === 'document';
}

export function isUser(record: SearchItem): record is User {
    return 'role' in record;
}

export function isPayment(record: SearchItem): record is Payment {
    return 'sumToPay' in record;
}

export function isViewableFile(record: SearchItem): record is ViewableFile {
    return 'viewForms' in record && 'showPermission' in record;
}

export function isRecordOperation(record: SearchItem): record is RecordOperation {
    return 'recordOperationType' in record;
}

export function isInventory(record: SearchItem): record is Inventory {
    return 'inventoryState' in record;
}

export function isCapture(record: SearchItem): record is Capture {
    return 'captureWay' in record;
}

export function isMatch(record: SearchItem): record is Match {
    return 'matchState' in record;
}

export function isSeekedSeeking(record: SearchItem): record is SeekedSeeking {
    return 'kind' in record && record.kind === 'seeking' && !record.providersView;
}

export function isProvidedSeeking(record: SearchItem): record is ProvidedSeeking {
    return 'kind' in record && record.kind === 'seeking' && record.providersView;
}
